import type { Step } from 'react-joyride';

/**
 * Step definitions for the Portal's first-run walkthroughs. Each tour
 * is identified by an `id` that doubles as the localStorage seen-flag
 * key. Targets are CSS selectors — prefer stable hooks (`[data-testid]`
 * or `.role-based` class) over visual classnames that may change.
 *
 * Some steps need to navigate the Builder (switch the inner section
 * nav) or the outer tab strip (Devices / Upgrade) BEFORE the step's
 * target is in the DOM. react-joyride v3 supports a per-step `before`
 * async hook for exactly this — the tour pauses with `waiting=true`,
 * runs the hook, then proceeds. We implement those hooks by clicking
 * the relevant nav / tab button (whose testid is already mounted) and
 * yielding a couple of frames for React to paint the new content.
 */

export const POST_SIGNUP_TOUR_ID = 'post-signup-v1';

/**
 * Plays after a fresh signup completes. The customer is sitting on the
 * SignupResult screen, looking at "your tenant is provisioned, here's
 * your URL." We point them at the Customize CTA, then explain "Your
 * fleets" tab once they're signed in.
 */
export const POST_SIGNUP_STEPS: readonly Step[] = [
  {
    target: '[data-testid="signup-customize"]',
    title: 'Your tenant is live',
    content:
      'Click Customize to open the launcher editor. You can change the theme, tiles, and hero band before pointing any device at it.',
    placement: 'bottom',
  },
  {
    target: '[data-testid="signup-launcher-url"]',
    title: 'This is your launcher URL',
    content:
      'Point any kiosk-capable MDM Lockdown profile or kiosked Android browser at this URL. The first reload picks up whatever you publish.',
    placement: 'bottom',
  },
];

// v5 — adds a Telemetry step between Devices and Upgrade (the
// Telemetry outer tab landed after v4). Same Theme → Hero → Tiles →
// Preview path otherwise. Bumping the id re-fires the tour for
// users who already saw v4.
export const EDITOR_TOUR_ID = 'editor-v5';

/**
 * Click an element by its `data-testid` and yield two animation
 * frames so React's resulting render + the new section's mount
 * effects flush before joyride looks up the next step's target.
 * Two frames is the smallest reliable hand-off; one frame sometimes
 * loses the race against deeply-nested useEffect mounts.
 */
async function clickAndYield(testId: string): Promise<void> {
  const el = document.querySelector<HTMLButtonElement>(`[data-testid="${testId}"]`);
  el?.click();
  await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
  await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
}

/**
 * Plays the first time the embedded Builder mounts. Walks the
 * Theme → Hero → Tile grid sections (the three things a new admin
 * actually edits), pauses at the live preview, then hops to the
 * outer Devices + Upgrade tabs so they know where deployment and
 * paid tiers live.
 */
export const EDITOR_STEPS: readonly Step[] = [
  {
    target: '[data-testid="builder-nav-theme"]',
    title: 'Edit your launcher in sections',
    content:
      'Each row in this list is a slice of your launcher — Theme, Hero, Tile grid. We\'ll walk the most useful three. You can jump around any time.',
    placement: 'right',
  },
  {
    target: '[data-testid="theme-brand-fieldset"]',
    title: 'Set your brand colors',
    content:
      'Pick a Primary and an Accent. Both flow through to light AND dark mode automatically — Pro lets you customize the full per-surface palette per mode under Light palette / Dark palette tabs.',
    placement: 'right',
    before: async () => clickAndYield('builder-nav-theme'),
  },
  {
    target: '[data-testid="hero-variant-fieldset"]',
    title: 'Choose a hero variant',
    content:
      'The slab between the header and tile grid. Featured tiles (the default) shows up to four large launches; Clock / Weather / Rich text / None are alternatives — pick one to match how the device is used.',
    placement: 'right',
    before: async () => clickAndYield('builder-nav-hero'),
  },
  {
    target: '[data-testid="add-tile-group"]',
    title: 'Build the tile grid',
    content:
      'App, Web, Folder, Spacer, Divider, Agent action — each button adds a tile of that type. Drag the grip to reorder; the inspector below each tile sets its label, icon, and target.',
    placement: 'right',
    before: async () => clickAndYield('builder-nav-tiles'),
  },
  {
    target: '[data-testid="live-pane"]',
    title: 'Live preview',
    content:
      'Edits stream into this iframe within ~200 ms. Pick a real device from the dropdown (Zebra ET40, Galaxy Tab Active, etc.) to spot layout issues at the exact resolution your fleet runs.',
    placement: 'left',
  },
  {
    target: '[data-testid="broadcasts-editor"]',
    title: 'Push messages to the fleet',
    content:
      "Post a short message (info / warning / critical) and devices pick it up on their next poll — shows up in the launcher's bell-icon panel within a minute. Set an expiration so old messages don't pile up. Pro tier.",
    placement: 'top',
    before: async () => clickAndYield('editor-tab-broadcasts'),
  },
  {
    target: '[data-testid="devices-panel"]',
    title: 'Deploy to devices',
    content:
      'Devices tab has a QR sticker for the launcher URL + a print sheet for 12-up stickers, plus three install paths (browser kiosk, MDM-pushed, sideload APK). Pick whatever fits the fleet you have today.',
    placement: 'top',
    before: async () => clickAndYield('editor-tab-devices'),
  },
  {
    target: '[data-testid="telemetry-panel"]',
    title: 'Verify telemetry is flowing',
    content:
      "Enterprise tier. Every event your fleet flushes lands here within ~30 seconds so you can verify the pipeline is working before pointing telemetry.endpoint at your own stack. Empty until your first device flushes — devices ship batches on a 60-second cadence.",
    placement: 'top',
    before: async () => clickAndYield('editor-tab-telemetry'),
  },
  {
    target: '[data-testid="audit-panel"]',
    title: 'Audit log for compliance',
    content:
      "Enterprise tier. Tamper-evident record of admin actions, gesture/PIN events, and runtime state changes — pre-filtered from the telemetry stream and CSV-exportable. Flip adminAccess.auditLogEnabled on a device to start capturing.",
    placement: 'top',
    before: async () => clickAndYield('editor-tab-audit'),
  },
  {
    target: '[data-testid="upgrade-panel"]',
    title: 'Paid tiers',
    content:
      'Free covers a single-tablet kiosk. Pro adds full theming, broadcasts, scheduled profiles. Enterprise adds remote config, telemetry export, SSO display, signage mode. Switch any time — no contract.',
    placement: 'top',
    before: async () => clickAndYield('editor-tab-upgrade'),
  },
];
